import React from "react";
import { Layers } from "lucide-react";
import { CATEGORIES } from "../lib/categories";

// "All" needs an icon + color so its chip matches the visual rhythm of
// the per-category chips. Using the brand green keeps it neutral.
const ALL = { key: "all", label: "All", short: "All", icon: Layers, color: "#4A7C59" };

// Private parks aren't a Google Places category, they're a separate
// product surface (hosted by individual owners). Render as an emoji
// chip — same dimensions and colors as the other chips so the row
// stays visually coherent. The `emoji` field is read in the render
// path below as a fallback when `icon` is missing.
const PRIVATE = {
  key: "private",
  label: "Private Parks",
  short: "Private",
  emoji: "🏡",
  color: "#4A7C59",
};

/**
 * Horizontal scrollable category filter chips for the search page.
 * Matches the Home page chip style exactly:
 *   - colored circular icon badge to the LEFT of the label
 *   - icon white, badge background = category accent color
 *   - selected chip flips background to the category color and the badge
 *     becomes a light translucent circle for contrast
 */
const CategoryFilterBar = ({ value = "all", onChange }) => {
  const chips = [ALL, PRIVATE, ...CATEGORIES];
  return (
    <div
      data-testid="category-filter-bar"
      className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide snap-x"
    >
      {chips.map((c) => {
        const active = value === c.key;
        const Icon = c.icon;
        const tint = c.color || "#4A7C59";
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`category-chip-${c.key}`}
            onClick={() => onChange?.(c.key)}
            aria-pressed={active}
            className={[
              "shrink-0 snap-start inline-flex items-center gap-1.5 rounded-full border pl-1.5 pr-3.5 py-1 text-sm font-semibold transition-all active:scale-[0.97]",
              active
                ? "text-white border-transparent shadow-warm"
                : "bg-white text-brand-ink border-brand-border-warm hover:shadow-warm hover:-translate-y-0.5",
            ].join(" ")}
            style={active ? { backgroundColor: tint } : undefined}
          >
            <span
              className={[
                "w-6 h-6 rounded-full flex items-center justify-center text-white",
                active ? "bg-white/25" : "",
              ].join(" ")}
              style={!active ? { backgroundColor: tint } : undefined}
            >
              {Icon ? (
                <Icon className="w-3.5 h-3.5" strokeWidth={2.6} />
              ) : c.emoji ? (
                // Emoji needs a slight font-size + line-height boost so it
                // optically centers against the lucide-stroke icons.
                <span className="text-[13px] leading-none" aria-hidden>
                  {c.emoji}
                </span>
              ) : null}
            </span>
            {c.short || c.label}
          </button>
        );
      })}
    </div>
  );
};

export default CategoryFilterBar;
